import { CSSProperties, useEffect, useRef, useState } from 'react';
import './ProductCameraAssistant.css';

export type CameraRegion = { x: number; y: number; width: number; height: number };
export type ProductScan = { id: number; code: string; bounds?: CameraRegion };
export type CameraProduct = { seq: number; name: string; hasImage: boolean };
export type ProductCameraActions = {
  current: () => Promise<CameraProduct | null>;
  resolve: (code: string) => Promise<CameraProduct>;
  expose: (seq: number) => Promise<CameraProduct>;
  capture: (seq: number, region: CameraRegion) => Promise<unknown>;
};
type Prompt = { step: 'expose' | 'capture'; product: CameraProduct; deadline: number; region: CameraRegion };
export function productCaptureRegion(): CameraRegion {
  return { x: .1, y: .275, width: .8, height: .45 };
}
function qrFocusRegion(bounds?: CameraRegion): CameraRegion | undefined {
  if (!bounds || !Object.values(bounds).every(Number.isFinite) || bounds.width <= 0 || bounds.height <= 0) return;
  const paddingX = bounds.width * .15 + .01, paddingY = bounds.height * .15 + .01;
  const x = Math.max(0, bounds.x - paddingX), y = Math.max(0, bounds.y - paddingY);
  const right = Math.min(1, bounds.x + bounds.width + paddingX), bottom = Math.min(1, bounds.y + bounds.height + paddingY);
  if (right <= x || bottom <= y) return;
  return { x, y, width: right - x, height: bottom - y };
}

// Shared by the PC canvas and the native sender; neither opens a second camera.
export default function ProductCameraAssistant({ active, scan, actions, onChanged, ownerDocument = document, keepErrorsVisible = false }: {
  active: boolean; scan?: ProductScan; actions: ProductCameraActions; onChanged?: () => void; ownerDocument?: Document; keepErrorsVisible?: boolean;
}) {
  const [prompt, setPrompt] = useState<Prompt | null>(null);
  const [remaining, setRemaining] = useState(3);
  const [progress, setProgress] = useState(1);
  const [working, setWorking] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [error, setError] = useState('');
  const generation = useRef(0);
  const pending = useRef(false);
  const lastCode = useRef('');
  const lastExposure = useRef<number | null>(null);
  const latest = useRef({ actions, onChanged, active }); latest.current = { actions, onChanged, active };
  const show = (value: Prompt | null) => { setPrompt(value); setRemaining(3); setProgress(1); };
  const cancel = () => { generation.current++; pending.current = false; show(null); setWorking(false); setResolving(false); };
  useEffect(() => {
    if (!error || keepErrorsVisible) return;
    const timeout = window.setTimeout(() => setError(''), 3000);
    return () => window.clearTimeout(timeout);
  }, [error, keepErrorsVisible]);

  useEffect(() => {
    if (!active) { cancel(); lastCode.current = ''; }
    return () => { generation.current++; pending.current = false; };
    // Active transitions invalidate all pending responses and countdowns.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  useEffect(() => {
    const hide = () => { if (ownerDocument.hidden) cancel(); };
    ownerDocument.addEventListener('visibilitychange', hide);
    return () => ownerDocument.removeEventListener('visibilitychange', hide);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerDocument]);

  useEffect(() => {
    if (!active || !scan) return;
    if (!scan.code) { lastCode.current = ''; return; }
    if (!/^fanbird:product:v1:[1-9][0-9]{0,9}$/.test(scan.code) || lastCode.current === scan.code || pending.current || ownerDocument.hidden) return;
    lastCode.current = scan.code;
    const token = ++generation.current;
    pending.current = true; setError(''); setResolving(true);
    latest.current.actions.resolve(scan.code).then(product => {
      if (token !== generation.current || !latest.current.active) return;
      setResolving(false);
      show({ step: 'expose', product, deadline: Date.now() + 3000, region: productCaptureRegion() });
    }).catch(reason => { if (token === generation.current) { pending.current = false; setResolving(false); setError(reason.message || 'QR 상품을 확인하지 못했습니다.'); } });
  }, [scan, active, ownerDocument]);

  useEffect(() => {
    if (!active) return;
    let disposed = false;
    let polling = false;
    const poll = async () => {
      if (polling || pending.current || ownerDocument.hidden) return;
      const token = generation.current; polling = true;
      try {
        const product = await latest.current.actions.current();
        if (disposed || token !== generation.current || pending.current) return;
        if (lastExposure.current !== (product?.seq ?? null)) {
          lastExposure.current = product?.seq ?? null;
          if (product && !product.hasImage) {
            pending.current = true;
            show({ step: 'capture', product, deadline: Date.now() + 3000, region: productCaptureRegion() });
          }
        }
      } catch { /* Transient polling failure never changes a product or registers a photo. */ }
      finally { polling = false; }
    };
    void poll(); const interval = window.setInterval(poll, 1500);
    return () => { disposed = true; window.clearInterval(interval); };
  }, [active, ownerDocument]);

  useEffect(() => {
    if (!prompt || !active) return;
    const token = generation.current;
    let submitted = false;
    const tick = async () => {
      if (submitted || token !== generation.current || ownerDocument.hidden || !latest.current.active) return;
      const milliseconds = Math.max(0, prompt.deadline - Date.now());
      const seconds = Math.ceil(milliseconds / 1000);
      setRemaining(seconds);
      setProgress(milliseconds / 3000);
      if (seconds) return;
      submitted = true; setWorking(true);
      try {
        if (prompt.step === 'expose') {
          const product = await latest.current.actions.expose(prompt.product.seq);
          if (token !== generation.current || !latest.current.active) return;
          lastExposure.current = product.seq; latest.current.onChanged?.();
          if (!product.hasImage) { show({ ...prompt, step: 'capture', product, deadline: Date.now() + 3000 }); return; }
        } else {
          const product = await latest.current.actions.current();
          if (token !== generation.current || !latest.current.active) return;
          if (product?.seq !== prompt.product.seq) {
            lastExposure.current = null;
            throw new Error('노출 상품이 변경되어 자동 캡처를 취소했습니다.');
          }
          if (!product.hasImage) await latest.current.actions.capture(product.seq, prompt.region);
          if (token !== generation.current) return;
          latest.current.onChanged?.();
        }
        if (token === generation.current) { pending.current = false; show(null); }
      } catch (reason: any) {
        if (token === generation.current) { pending.current = false; show(null); setError(reason.message || '요청을 처리하지 못했습니다.'); }
      } finally { if (token === generation.current) setWorking(false); }
    };
    const interval = window.setInterval(tick, 100);
    return () => window.clearInterval(interval);
  }, [prompt, active, ownerDocument]);

  if (!active) return null;
  const focus = scan?.code && prompt?.step !== 'capture' ? qrFocusRegion(scan.bounds) : undefined;
  return <>
    {keepErrorsVisible && resolving && <div className="product-camera-prompt" role="status"><p>QR 상품 확인 중…</p><button type="button" onClick={cancel}>취소</button></div>}
    {focus && <div className="product-camera-qr-focus" aria-label="인식된 QR 위치" style={{
      left: `${focus.x * 100}%`, top: `${focus.y * 100}%`, width: `${focus.width * 100}%`, height: `${focus.height * 100}%`,
    }} />}
    {prompt?.step === 'capture' && <div aria-label="상품 사진 캡처 영역" className="product-camera-region" style={{
      left: `${prompt.region.x * 100}%`, top: `${prompt.region.y * 100}%`, width: `${prompt.region.width * 100}%`, height: `${prompt.region.height * 100}%`,
    } as CSSProperties}><span>이 영역이 상품 사진으로 저장됩니다</span>
      <strong className="product-camera-countdown" aria-live="polite" aria-label={working ? '촬영 중' : `촬영까지 ${remaining}초`}>{working ? '촬영 중' : remaining}</strong>
    </div>}
    {prompt && <section className={`product-camera-prompt${prompt.step === 'capture' ? ' product-camera-prompt--capture' : ''}`} role="dialog" aria-modal="false" aria-label={prompt.step === 'expose' ? 'QR 상품 판매 노출' : '상품 사진 자동 캡처'}>
      <div><strong>{prompt.product.name}</strong><p aria-live="polite">{working ? prompt.step === 'expose' ? '판매 노출로 전환 중…' : '상품 사진 저장 중…'
        : prompt.step === 'expose' ? `${remaining}초 후 판매 노출됩니다` : '표시된 영역을 상품 사진으로 저장합니다'}</p></div>
      <button type="button" onClick={cancel} disabled={working}>취소</button>
      <div className="product-camera-progress" role="progressbar" aria-label="자동 실행까지 남은 시간" aria-valuemin={0} aria-valuemax={3} aria-valuenow={remaining}>
        <span style={{ transform: `scaleX(${progress})` }} />
      </div>
    </section>}
    {error && <div className="product-camera-prompt" role="alert"><p>{error}</p><button type="button" onClick={() => setError('')}>닫기</button>
      {!keepErrorsVisible && <div className="product-camera-progress"><span key={error} className="product-camera-progress--notice" /></div>}
    </div>}
  </>;
}
